import { createClient } from '@/lib/supabase/client';

/**
 * Every email address the site collects, from whatever form collected it.
 *
 * Stored in Supabase rather than the browser: a list that lives in
 * localStorage exists in exactly one browser profile and disappears when the
 * visitor clears site data, which makes it useless as a mailing list.
 */

export type SubscriberSource = 'assessment' | 'newsletter';

export interface Subscriber {
  id: string;
  email: string;
  source: SubscriberSource | string;
  name: string;
  context: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export const SUBSCRIBERS_TABLE = 'subscribers';

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string) {
  return EMAIL_PATTERN.test(value.trim());
}

export type SaveResult =
  | { ok: true; duplicate: boolean }
  | { ok: false; reason: 'invalid' | 'unconfigured' | 'failed'; message: string };

/** Postgres unique-violation. Re-submitting the same form is not an error to
 *  show the visitor — they are on the list either way. */
const UNIQUE_VIOLATION = '23505';

export async function addSubscriber(input: {
  email: string;
  source: SubscriberSource;
  name?: string;
  context?: string;
  details?: Record<string, unknown>;
}): Promise<SaveResult> {
  const email = input.email.trim().toLowerCase();
  if (!isValidEmail(email)) {
    return { ok: false, reason: 'invalid', message: 'Please enter a valid email address.' };
  }

  const supabase = createClient();
  if (!supabase) {
    return {
      ok: false,
      reason: 'unconfigured',
      message: 'Sign-up is not available right now. Please email info@noventilabs.com.',
    };
  }

  // supabase-js rejects rather than resolving with an error when the request
  // itself fails — DNS, offline, a blocked host. Uncaught, that rejection
  // escaped to the caller and left the form stuck on "Saving…" with nothing
  // shown, which is indistinguishable from the feature being broken.
  let error: { code?: string; message: string } | null = null;
  try {
    const result = await supabase.from(SUBSCRIBERS_TABLE).insert({
      email,
      source: input.source,
      name: input.name?.trim() ?? '',
      context: input.context ?? '',
      details: input.details ?? {},
    });
    error = result.error;
  } catch (thrown) {
    console.error('Could not reach the subscriber store:', thrown);
    return {
      ok: false,
      reason: 'failed',
      message:
        'We could not reach our server. Please check your connection, or email info@noventilabs.com.',
    };
  }

  if (error) {
    if (error.code === UNIQUE_VIOLATION) return { ok: true, duplicate: true };
    console.error('Could not save subscriber:', error.message);
    return {
      ok: false,
      reason: 'failed',
      message: 'That could not be saved. Please try again, or email info@noventilabs.com.',
    };
  }
  return { ok: true, duplicate: false };
}

export async function loadSubscribers(): Promise<{ subscribers: Subscriber[]; error: string }> {
  const supabase = createClient();
  if (!supabase) return { subscribers: [], error: 'Supabase is not configured.' };

  const { data, error } = await supabase
    .from(SUBSCRIBERS_TABLE)
    .select('*')
    .order('created_at', { ascending: false });

  if (error) return { subscribers: [], error: error.message };

  const subscribers = (data ?? []).map((row: Record<string, unknown>) => ({
    id: String(row.id),
    email: String(row.email ?? ''),
    source: String(row.source ?? 'newsletter'),
    name: String(row.name ?? ''),
    context: String(row.context ?? ''),
    details: (row.details as Record<string, unknown>) ?? {},
    createdAt: String(row.created_at ?? ''),
  }));
  return { subscribers, error: '' };
}

export async function deleteSubscriber(id: string): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(SUBSCRIBERS_TABLE).delete().eq('id', id);
  return error ? error.message : null;
}

/** CSV for the admin's "export" button. Values are quoted and internal quotes
 *  doubled, so an address book with a comma in a name cannot corrupt the file. */
export function subscribersToCsv(subscribers: Subscriber[]): string {
  const cell = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const rows = [
    ['Email', 'Source', 'Name', 'Context', 'Date'].map(cell).join(','),
    ...subscribers.map((s) =>
      [s.email, s.source, s.name, s.context, s.createdAt].map(cell).join(',')
    ),
  ];
  return rows.join('\n');
}
